/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { Card, CardContent, Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import { withTheme } from '@emotion/react';
import { useDispatch, useSelector } from 'react-redux';
import { selectAuthUser, selectOrganizationId } from 'modules/common/auth/selectors';
import ORDER_BY from 'modules/common/constants/order-by';
import PAGINATION from 'modules/common/constants/pagination-limits';
import { Loader } from 'modules/common/components';
import { selectLoader } from '../selectors';
import ConfigureAlert from './configure-alert';
import AlertTableView from './alert-table';
import LogTableView from './log-table';
import AlertDataDrawerView from './alert-drawer';
import { alertActions } from '../slice';
import ActionButton from '../../../../../common/components/action-button';
import LogDrawerView from './log-drawer';
import { notificationActions } from '../../../../../common/notifications/slice';
import { selectEnableHotelGroups, selectHotelName, selectTabList } from '../../../../selectors';

/**
 * Alert widget component
 * @param {*} theme - theme
 * @param {*} value
 * @param {*} active - is tab active
 * @returns
 */
const AlertWidget = ({ theme, value, active }) => {
  const dispatch = useDispatch();
  //
  const loading = useSelector(selectLoader);
  const organizationId = useSelector(selectOrganizationId);
  const hotel = useSelector(selectHotelName);
  const enableHotelGroups = useSelector(selectEnableHotelGroups);
  const authUser = useSelector(selectAuthUser);
  const TAB_VALUE = useSelector(selectTabList);
  //
  const [selectedAlertId, setSelectedAlertId] = useState({});
  const [open, setOpen] = useState(false);
  const [show, isShow] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [notificationId, setNotificationId] = useState({});
  const [showNotification, setShowNotification] = useState(false);
  //
  useEffect(() => {
    if (hotel !== null && !enableHotelGroups) {
      dispatch(
        alertActions.getAlertTableData({
          organizationId,
          userId: authUser?.id,
          query: `hotelIds=${hotel?.hotelCode}&limit=${PAGINATION.USER_ALERT_COUNT}&page=0&sortBy=createdAt:${ORDER_BY.DESCENDING}`,
        })
      );
    }
    setShowNotification(false);
  }, [organizationId, authUser, hotel, enableHotelGroups]);
  //
  useEffect(() => {
    dispatch(
      alertActions.getSubscriptions({
        organizationId,
        userId: authUser?.id,
        query: `limit=${PAGINATION.USER_ALERT_COUNT}`,
      })
    );
  }, [organizationId, authUser]);
  //
  useEffect(() => {
    dispatch(
      alertActions.getAlertParam({
        query: `limit=${PAGINATION.ALERT_PARAMETER_COUNT}`,
      })
    );
    dispatch(notificationActions.resetNotification());
  }, [dispatch]);
  //
  return (
    active && (
      <Loader loading={loading}>
        <Grid
          container
          sx={{
            display: value === TAB_VALUE.ALERT ? '' : 'none',
            mt: -5,
          }}
        >
          <>
            <Card sx={{ width: '100%' }}>
              <CardContent
                sx={{
                  backgroundColor: theme.palette.background.default,
                }}
              >
                <Grid container direction="row" justifyContent="flex-end">
                  {/* Hidden for 4.0 release */}
                  {false && (
                    <ActionButton
                      onClick={() => setOpen(true)}
                      label="Create Alert"
                      disabled={show}
                      sx={{ mt: 1 }}
                    />
                  )}
                </Grid>
                <Grid container direction="row" spacing={2} justifyContent="space-between">
                  <Grid item sx={{ flexGrow: 1, flexBasis: 0, minWidth: 0 }}>
                    <LogTableView
                      setShowNotification={setShowNotification}
                      setNotificationId={setNotificationId}
                      isDrawerOpen={showNotification}
                    />
                    <AlertTableView
                      isShow={isShow}
                      setSelectedAlertId={setSelectedAlertId}
                      setIsEditable={setIsEditable}
                      isDrawerOpen={showNotification}
                    />
                  </Grid>
                  <AlertDataDrawerView
                    show={show}
                    isShow={isShow}
                    alertId={selectedAlertId}
                    isEditable={isEditable}
                  />
                  <LogDrawerView
                    show={showNotification}
                    setShowNotification={setShowNotification}
                    notification={notificationId}
                  />
                </Grid>
              </CardContent>
            </Card>
            <ConfigureAlert open={open} onClose={() => setOpen(false)} />
          </>
        </Grid>
      </Loader>
    )
  );
};
//
export default withTheme(AlertWidget);
